import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const WD = [["Mo", 0], ["Di", 1], ["Mi", 2], ["Do", 3], ["Fr", 4], ["Sa", 5], ["So", 6]];
const errText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Speichern fehlgeschlagen";
};

export const BulkEditPanel = ({ slug, start, end, setStart, setEnd, onSaved }) => {
  const [price, setPrice] = useState("");
  const [clearPrice, setClearPrice] = useState(false);
  const [priceLong, setPriceLong] = useState("");
  const [clearLong, setClearLong] = useState(false);
  const [minStay, setMinStay] = useState("");
  const [clearMin, setClearMin] = useState(false);
  const [avail, setAvail] = useState("keep");
  const [weekdays, setWeekdays] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleWd = (w) => setWeekdays((p) => (p.includes(w) ? p.filter((x) => x !== w) : [...p, w]));

  const save = async () => {
    if (!start || !end) { toast.error("Bitte Zeitraum wählen"); return; }
    if (end < start) { toast.error("Enddatum darf nicht vor dem Startdatum liegen"); return; }
    if ((price !== "" && Number(price) < 0) || (priceLong !== "" && Number(priceLong) < 0)) { toast.error("Preis darf nicht negativ sein"); return; }
    if (minStay !== "" && Number(minStay) < 1) { toast.error("Mindestaufenthalt mindestens 1 Nacht"); return; }
    const body = { start, end, weekdays };
    if (price !== "") body.price = Number(price); else if (clearPrice) body.clear_price = true;
    if (priceLong !== "") body.price_long = Number(priceLong); else if (clearLong) body.clear_price_long = true;
    if (minStay !== "") body.min_stay = Number(minStay); else if (clearMin) body.clear_min_stay = true;
    if (avail !== "keep") body.closed = avail === "closed";
    setSaving(true);
    try {
      const { data } = await api.post(`/rates/${slug}/days`, body);
      toast.success(`${data.changed_days} Tage gespeichert – sofort auf der Homepage aktiv`);
      if (avail === "open" && data.booked_days) toast.info(`${data.booked_days} Nächte bleiben wegen bestehender Buchungen belegt`);
      setPrice(""); setPriceLong(""); setClearLong(false); setMinStay(""); setClearPrice(false); setClearMin(false); setAvail("keep");
      onSaved();
    } catch (e) { toast.error(errText(e)); }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="bulk-edit-panel">
      <h3 className="font-semibold">Zeitraum bearbeiten</h3>
      <p className="text-xs text-muted-foreground">Tage im Kalender anklicken (zweiter Klick = Ende) oder Datum eingeben. Leere Felder bleiben unverändert.</p>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs">Von<Input type="date" value={start || ""} onChange={(e) => setStart(e.target.value)} data-testid="bulk-start"/></label>
        <label className="text-xs">Bis<Input type="date" value={end || ""} onChange={(e) => setEnd(e.target.value)} data-testid="bulk-end"/></label>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="bulk-weekdays">
        {WD.map(([l, w]) => (
          <button key={w} type="button" onClick={() => toggleWd(w)} data-testid={`bulk-wd-${w}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${weekdays.includes(w) ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"}`}>{l}</button>
        ))}
        <span className="text-[11px] text-muted-foreground self-center ml-1">{weekdays.length ? "nur diese Wochentage" : "alle Tage"}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs block">Preis 1 Nacht (€)
          <Input type="number" min="0" step="1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="unverändert" data-testid="bulk-price"/>
        </label>
        <label className="text-xs block">Preis ab 3 Nächten (€)
          <Input type="number" min="0" step="1" value={priceLong} onChange={(e) => setPriceLong(e.target.value)} placeholder="unverändert" data-testid="bulk-price-long"/>
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={clearPrice} disabled={price !== ""} onChange={(e) => setClearPrice(e.target.checked)} data-testid="bulk-clear-price"/>Tagespreis 1 Nacht entfernen</label>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={clearLong} disabled={priceLong !== ""} onChange={(e) => setClearLong(e.target.checked)} data-testid="bulk-clear-price-long"/>Tagespreis ab 3 Nächten entfernen</label>
      <label className="text-xs block">Mindestaufenthalt (Nächte)
        <Input type="number" min="1" step="1" value={minStay} onChange={(e) => setMinStay(e.target.value)} placeholder="unverändert" data-testid="bulk-min"/>
      </label>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={clearMin} disabled={minStay !== ""} onChange={(e) => setClearMin(e.target.checked)} data-testid="bulk-clear-min"/>Mindestaufenthalt des Tages entfernen</label>
      <div className="text-xs">Verfügbarkeit
        <div className="grid grid-cols-3 gap-1.5 mt-1">
          {[["keep", "unverändert"], ["open", "Freigeben"], ["closed", "Sperren"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setAvail(v)} data-testid={`bulk-avail-${v}`}
              className={`py-1.5 rounded-lg border text-xs transition-colors ${avail === v ? (v === "closed" ? "bg-red-600 text-white border-red-600" : "bg-primary text-white border-primary") : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="w-full rounded-xl" data-testid="bulk-save">{saving ? "Speichere…" : "Speichern"}</Button>
      <p className="text-[11px] text-muted-foreground">Bestehende Buchungen haben immer Vorrang: Freigeben macht gebuchte Nächte nicht buchbar.</p>
    </div>
  );
};
